/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
  Box,
  Theme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  OutlinedIcons,
  getFormattedDateMonthDay,
  useApiClient,
} from '@marageti/z4-lib';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';
import { ConversationType } from '@marageti/z4-sdk/lib/travel';
import StatusButton from './action-buttons/reminder-button-status';
import RescheduleButton from './action-buttons/reminder-button-reschedule';
import SendMessageButton from './action-buttons/reminder-button-send-message';
import EditButton from './action-buttons/reminder-button-edit';
import ReminderContextMenu from './action-buttons/reminder-context-menu';

const { OutlinedUserIcon } = OutlinedIcons;

type AssigneeOption = {
  title: string;
  value: string;
};

type ReminderCardViewProps = {
  ticket: Ticket;
  assignees?: AssigneeOption[];
  isRescheduling?: boolean;
  isTogglingComplete?: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onReschedule: () => void;
  onToggleComplete: () => void;
  onDismiss: () => void;
};

const ReminderCardView = ({
  ticket,
  assignees = [],
  isRescheduling = false,
  isTogglingComplete = false,
  onEdit,
  onDuplicate,
  onReschedule,
  onToggleComplete,
  onDismiss,
}: ReminderCardViewProps) => {
  const apiClient = useApiClient();
  const [travelerToZicassoId, setTravelerToZicassoId] = useState<string | null>(
    null,
  );
  const {
    title, description, dueAt, status, tripRequestId, assignees: ticketAssignees,
  } = ticket;

  const primaryAssignee = ticketAssignees?.[0];
  const assigneeName = primaryAssignee
    ? (primaryAssignee.name
      || (assignees.find((a) => a.value === primaryAssignee.id)?.title)
      || 'Not Found')
    : 'Unassigned';

  // Media queries
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('desktop'));
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isXl = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

  // Formatted due date
  const formattedDueDate = getFormattedDateMonthDay(dueAt) || '';

  // Compare dates without time to determine if overdue
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(dueAt);
  dueDate.setHours(0, 0, 0, 0);

  // Statuses
  const isCompleted = status === 'COMPLETED';
  const isCanceled = status === 'CANCELED';
  const isOpen = status === 'PENDING';
  const isDismissed = status === 'CANCELED';
  const isOverdue = status === 'PENDING' && dueDate < today;

  // Get traveler to zicasso id for message link
  useEffect(() => {
    if (tripRequestId) {
      const fetchTravelerToZicassoId = async () => {
        try {
          const conversationSummaries = await apiClient.tripRequestsClient.getConversationSummaries(
            tripRequestId,
          );
          const travelerConversation = conversationSummaries?.find(
            (s) => s.conversationType === ConversationType.TravelerToZicasso,
          );
          setTravelerToZicassoId(travelerConversation?.conversationId || null);
        } catch (err) {
          // Silently fail - the link will just go to the trip request page
          setTravelerToZicassoId(null);
        }
      };
      fetchTravelerToZicassoId();
    }
  }, [tripRequestId, apiClient]);

  // Get message link
  const messageLink = tripRequestId && travelerToZicassoId
    ? `/trip-requests/${tripRequestId}/messages/${travelerToZicassoId}`
    : tripRequestId
      ? `/trip-requests/${tripRequestId}`
      : '#';

  return (
    <Card
      {...(tripRequestId && {
        component: Link,
        to: `/trip-requests/${tripRequestId}`,
        sx: {
          '&:hover': {
            bgcolor: 'grey.A100',
          },
          textDecoration: 'none',
        },
      })}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            {/* Header with status icon and title */}

            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box sx={{}}>
                <StatusButton
                  isOpen={isOpen}
                  isCompleted={isCompleted}
                  isCanceled={isCanceled}
                  isTogglingComplete={isTogglingComplete}
                  onToggleComplete={onToggleComplete}
                />
              </Box>

              {/* More Actions */}
              <ReminderContextMenu
                ticketId={ticket.id}
                tripRequestId={tripRequestId}
                isMobile
                isDismissed={isDismissed}
                messageLink={messageLink}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onDismiss={onDismiss}
              />
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography
                    variant="bodyBold"
                    sx={{
                      textDecoration: isCompleted ? 'line-through' : 'none',
                    }}
                  >
                    {title}
                  </Typography>
                </Stack>
                {description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {description}
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Actions */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={2}
            >
              {/* Date Button - Reschedule */}
              <RescheduleButton
                isOverdue={isOverdue}
                isRescheduling={isRescheduling}
                formattedDueDate={formattedDueDate}
                onReschedule={onReschedule}
              />

              {/* Details */}
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIcon
                  component={OutlinedUserIcon}
                  sx={{ width: 18, height: 18 }}
                />
                <Typography variant="caption" color="common.black">
                  {assigneeName}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          // Desktop view
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            columns={12}
          >
            <Grid
              item
              xs={isXl ? 6 : 5.5}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              gap={2}
            >
              {/* Status Icon */}
              <StatusButton
                isOpen={isOpen}
                isCompleted={isCompleted}
                isCanceled={isCanceled}
                isTogglingComplete={isTogglingComplete}
                onToggleComplete={onToggleComplete}
              />
              {/* Title and Description */}
              <Stack direction="column" overflow="hidden">
                <Typography
                  variant="bodyBold"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textDecoration: !isOpen ? 'line-through' : 'none',
                    color: !isOpen ? 'text.secondary' : 'black',
                  }}
                >
                  {title}
                </Typography>
                {description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    noWrap
                  >
                    {description}
                  </Typography>
                )}
              </Stack>
            </Grid>

            {/* Assignee */}
            <Grid item xs={isMd ? 2.5 : 2}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                <SvgIcon
                  component={OutlinedUserIcon}
                  sx={{ width: 20, height: 20 }}
                />
                <Typography variant="body">{assigneeName}</Typography>
              </Stack>
            </Grid>

            {/* Actions */}
            <Grid
              item
              xs={isXl ? 3 : 3.5}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Box sx={{ minWidth: 100, mr: isXl ? 3 : 2 }}>
                {/* Date Button - Reschedule */}
                <RescheduleButton
                  isOverdue={isOverdue}
                  isRescheduling={isRescheduling}
                  formattedDueDate={formattedDueDate}
                  onReschedule={onReschedule}
                />
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">

                {/* View Trip Request */}
                <SendMessageButton
                  tripRequestId={tripRequestId}
                  messageLink={messageLink}
                />

                {/* Edit */}
                <EditButton onEdit={onEdit} />

                {/* More Actions */}
                <ReminderContextMenu
                  ticketId={ticket.id}
                  tripRequestId={tripRequestId}
                  messageLink={messageLink}
                  isMobile={false}
                  isDismissed={isDismissed}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onDismiss={onDismiss}
                />
              </Stack>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default ReminderCardView;
